// CaptureDocTypePicker — the "Capture as Estimate / Invoice / Both" chooser, the
// FIRST step inside every capture pop-up (Digital signature, Document upload,
// Send for approval) and also changeable inside the Legal Document modal. Picking
// the track here decides which legal-doc track(s) the signature / photo / send
// produces. Each option carries the split-shield glyph (estimate = LEFT half,
// invoice = RIGHT half, both = full) so it speaks the same language as the
// AuthorizedDot legal-safe indicator.

import { Box, Group, SegmentedControl, Text } from '@mantine/core';
import { TrackShield } from './TrackShield';

export type CaptureDocType = 'estimate' | 'invoice' | 'both';

export function CaptureDocTypePicker({
  value,
  onChange,
  label = 'Capture as',
  hint,
  disabled = false,
}: {
  value: CaptureDocType;
  onChange: (v: CaptureDocType) => void;
  label?: string;
  hint?: string;
  disabled?: boolean;
}) {
  const opt = (track: CaptureDocType, text: string) => ({
    value: track,
    label: (
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
        <TrackShield track={track} size={13} />
        {text}
      </span>
    ),
  });
  return (
    <Box style={{ padding: '10px 12px', borderRadius: 8, background: 'rgba(99,102,241,0.06)', border: '1px solid rgba(99,102,241,0.30)' }}>
      <Group justify="space-between" align="center" mb={6} wrap="nowrap" gap={8}>
        <Text size="xs" c="#A5B4FC" fw={600} tt="uppercase" style={{ letterSpacing: 0.5 }}>{label}</Text>
        {hint && <Text size="xs" c="#71717A" style={{ flexShrink: 0 }}>{hint}</Text>}
      </Group>
      <SegmentedControl
        fullWidth
        disabled={disabled}
        value={value}
        onChange={(v) => onChange(v as CaptureDocType)}
        data={[opt('estimate', 'Estimate'), opt('invoice', 'Invoice'), opt('both', 'Both')]}
        styles={{
          root: { background: '#0C0C0E', border: '1px solid #27272A' },
          indicator: { background: 'rgba(99,102,241,0.25)' },
          label: { color: '#F4F4F5' },
        }}
      />
    </Box>
  );
}
